import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { LoginIdentifyStepComponent } from './login-identify-step.component';
import { LoginAuthStepComponent } from './login-auth-step.component';
import { LoginSsoRedirectComponent } from './login-sso-redirect.component';
import { IdentifyResult } from '../../models/sso.models';
import { LoginResult } from '../../services/auth.service';
import { zufaelligerSlogan } from './slogans';

type LoginStep = 'identify' | 'auth' | 'sso-redirecting';

/**
 * Identifier-First-Login-Orchestrator.
 *
 * Drei Zustände:
 *   - identify: Email-Feld + Weiter + Passkey-Picker
 *   - auth: Passwort + Passkey + Footer-Links (nach kein-SSO-Match)
 *   - sso-redirecting: Spinner + Hinweis (vor window.location-Redirect)
 *
 * Die Sub-Components emittieren Events; dieser Orchestrator hält den
 * State und entscheidet über Übergänge.
 */
@Component({
  selector: 'app-login-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // ViewEncapsulation.None: die .t2d-login__*-Klassen sollen auch in den
  // Sub-Components (Identify/Auth/SsoRedirect) wirken, die identische
  // Form-Patterns nutzen. Der Namespace-Präfix verhindert Style-Leaks.
  encapsulation: ViewEncapsulation.None,
  imports: [LoginIdentifyStepComponent, LoginAuthStepComponent, LoginSsoRedirectComponent, RouterLink],
  styleUrl: './login-shell.scss',
  template: `
    <div class="t2d-login">
      <aside class="t2d-login__brand" aria-hidden="true">
        <div class="t2d-login__brand-inner">
          <div class="t2d-login__brand-logo">
            <img src="assets/logos/planaudit/logo/PlanAudit_Logo_Negativ_Bold.svg"
                 alt="PlanAudit Logo"
                 class="t2d-login__logo-img" />
          </div>
          <div class="t2d-login__brand-text">
            <p class="t2d-login__brand-quote">„{{ slogan() }}"</p>
          </div>
          <div class="t2d-login__brand-deco" aria-hidden="true"></div>
        </div>
      </aside>

      <section class="t2d-login__form-panel">
        <div class="t2d-login__form-inner">
          <div class="t2d-login__mobile-logo" aria-hidden="true">
            <img src="assets/logos/planaudit/logo/PlanAudit_Logo_Negativ_Bold.svg"
                 alt="PlanAudit Logo"
                 class="t2d-login__logo-img t2d-login__logo-img--mobile" />
          </div>

          @switch (step()) {
            @case ('identify') {
              <app-login-identify-step
                [email]="email()"
                (identified)="onIdentified($event)"
              />
            }
            @case ('auth') {
              <app-login-auth-step
                [email]="email()"
                (editEmail)="onEditEmail()"
                (passwordSubmitResult)="onPasswordResult($event)"
              />
            }
            @case ('sso-redirecting') {
              <app-login-sso-redirect [mandantName]="mandantName()" />
            }
          }

          <footer class="t2d-login__footer">
            © {{ currentYear }} TEC2DATE GmbH ·
            <a routerLink="/open-source-hinweise">Open-Source-Hinweise</a>
          </footer>
        </div>
      </section>
    </div>
  `,
})
export class LoginPageComponent {
  private readonly router = inject(Router);

  protected readonly step = signal<LoginStep>('identify');
  protected readonly email = signal('');
  protected readonly mandantName = signal('');
  protected readonly slogan = signal(zufaelligerSlogan());
  protected readonly currentYear = new Date().getFullYear();

  onIdentified(result: IdentifyResult): void {
    this.email.set(result.email);
    if (result.sso) {
      this.mandantName.set(result.sso.mandantName);
      this.step.set('sso-redirecting');
      // 50 ms Mini-Delay, damit der Redirect-State sichtbar wird, bevor
      // wir die Page abreißen.
      setTimeout(() => {
        window.location.href = result.sso!.authorizeUrl;
      }, 50);
      return;
    }
    this.step.set('auth');
  }

  onEditEmail(): void {
    this.step.set('identify');
  }

  onPasswordResult(result: LoginResult): void {
    if (result === 'two-factor-required') {
      this.router.navigate(['/login/2fa']);
    } else if (result === 'success') {
      this.router.navigate(['/dashboard']);
    }
    // 'passkey-2fa-required': wird inline in LoginAuthStepComponent behandelt;
    // die Komponente navigiert nach erfolgreicher Assertion selbst zu /dashboard.
  }
}
